import {
  Box,
  Button,
  Flex,
  Form,
  type OperationType,
  SubmitButton,
  Text,
  TextAreaInputField,
  theme,
} from '@/components';
import type { AttachmentsModel } from '@/features/attachment/_models/attachment';
import { useForm } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

import { MAX_FILE_UPLOAD_LENGTH } from '../../constants';
import { ReplyFormSchema } from '../../models/reply';
import type { FileEntry } from '../../models/upload';
import { CommentReplyUploadButton } from './CommentReplyUploadButton';
import { CommentReplyUploadList } from './CommentReplyUploadList';

type FormValues = z.infer<typeof ReplyFormSchema>;

type Props = {
  type: OperationType;
  initialValues?: Partial<FormValues>;
  attachments?: z.infer<typeof AttachmentsModel>;
  onSubmit: (values: FormValues) => void;
  onCancel?: () => void;
};

export const CommentReplyForm: FC<Props> = ({
  type,
  initialValues,
  onSubmit,
  onCancel,
}) => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    getValues,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(ReplyFormSchema),
  });
  const isEdit = type === 'edit';

  const fileEntries = watch('fileEntries') ?? [];

  const handleUploadFiles = useCallback(
    async (acceptedFiles: File[]) => {
      const fileReaders = acceptedFiles.map((file) => {
        return new Promise<FileEntry>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            resolve({
              file,
              attachment: null,
              url: e.target?.result as string,
            });
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      });

      const acceptedFileEntries = await Promise.all(fileReaders);

      const newFiles = (getValues('fileEntries') ?? [])
        .concat(acceptedFileEntries)
        .slice(0, MAX_FILE_UPLOAD_LENGTH);

      setValue('fileEntries', newFiles, {
        shouldDirty: true,
      });
    },
    [getValues, setValue],
  );

  const handleRemoveFile = useCallback(
    (url: string) => {
      const fileEntries = getValues('fileEntries');
      setValue(
        'fileEntries',
        fileEntries?.filter((entry) => entry.url !== url),
        {
          shouldDirty: true,
        },
      );
    },
    [getValues, setValue],
  );

  return (
    <Box
      {...(isEdit && {
        background: token.colorBgLayout,
        padding: 8,
        borderRadius: 6,
      })}
    >
      <Form
        leavePageAlertCondition={{
          isDirty,
          isSubmitting,
          isSubmitSuccessful,
        }}
        initialValues={initialValues}
        onFinish={handleSubmit(onSubmit)}
      >
        <Flex vertical gap={isEdit ? 'middle' : 'small'}>
          <TextAreaInputField
            name="content"
            label=""
            control={control}
            errors={errors}
            rows={3}
            placeholder={t('input-item-placeholder', {
              item: t('reply'),
            })}
            required
            style={{ marginBottom: 0 }}
          />
          {type === 'edit' && (
            <>
              <Flex vertical gap="small">
                <Text text={t('attachment-file')} color="secondary" />
                <CommentReplyUploadButton
                  buttonProps={{ text: t('upload'), type: 'default' }}
                  fileEntries={fileEntries}
                  uploadFiles={handleUploadFiles}
                />
              </Flex>
              <CommentReplyUploadList
                fileEntries={fileEntries}
                onRemove={handleRemoveFile}
              />
              <Flex justify="end" gap="small">
                <Button type="default" onClick={onCancel}>
                  {t('cancel')}
                </Button>
                <SubmitButton
                  text={t('save')}
                  loading={isSubmitting}
                  disabled={!isDirty}
                />
              </Flex>
            </>
          )}
          {type === 'new' && (
            <>
              <CommentReplyUploadList
                fileEntries={fileEntries}
                onRemove={handleRemoveFile}
              />
              <Flex gap="small" justify="flex-end">
                <CommentReplyUploadButton
                  buttonProps={{ text: t('attach-file'), type: 'link' }}
                  fileEntries={fileEntries}
                  uploadFiles={handleUploadFiles}
                />
                {onCancel && (
                  <Button type="default" onClick={onCancel}>
                    {t('cancel')}
                  </Button>
                )}
                <SubmitButton
                  text={t('reply')}
                  loading={isSubmitting}
                  disabled={!isDirty}
                />
              </Flex>
            </>
          )}
        </Flex>
      </Form>
    </Box>
  );
};
